import { formatDate } from "@angular/common";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormBuilder, FormControl, UntypedFormBuilder, UntypedFormControl, Validators } from "@angular/forms";
import { SelectionListItem } from "@ha/ui/forms";
import { UntilDestroy } from "@ngneat/until-destroy";

import { ConceptType } from "@ha/data/basic";
import { HaModalService } from "@ha/ui/common";
import { certificateIdentifierValidator, dateValidator } from "@ha/util/common";
import { Select } from "@ngxs/store";
import { Observable } from "rxjs";
import { CreateCertificateAndPersonResponse, CreateCertificateForm } from "../../models/create-certificate-person.model";
import { PermitTeamSelectors } from "../../states/permit-team.selectors";
import { CertificateIdentifierHintModalComponent } from "../certificate-identifier-hint/certificate-identifier-hint.component";

@UntilDestroy()
@Component({
    selector: "pmt-create-certificate",
    templateUrl: "./create-certificate.component.html",
    styleUrls: ["./create-certificate.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateCertificateComponent implements OnInit {
    public ConceptType = ConceptType;

    @Input()
    public requireEmailAddress: boolean;

    @Output()
    updateForm = new EventEmitter<CreateCertificateForm>();
    @Select(PermitTeamSelectors.createCertificateResponse)
    public createCertificateResponse$: Observable<CreateCertificateAndPersonResponse>;

    public form = this.fb.group({
        concept: [ConceptType.BrandfarligaArbeten, Validators.required],
        certificateIdentifier: ["", Validators.required],
        expirationDate: ["", [Validators.required, dateValidator()]],
    }, { validators: [certificateIdentifierValidator()] });

    event$ = this.form.valueChanges.subscribe((x: any) => {
        const formValues = this.form.getRawValue() as unknown as CreateCertificateForm;
        formValues.concept = this.form.get("concept").value as ConceptType;
        formValues.formValid = this.form.valid;
        this.updateForm.emit(formValues);
    });

    public get minDate(): string {
        var nowPlusOneDay = new Date();
        nowPlusOneDay.setDate(nowPlusOneDay.getDate() + 1);
        return formatDate(nowPlusOneDay, "yyyy-MM-dd", "en");
    }

    public get maxDate(): string {
        var nowPlusFiveYears = new Date();
        nowPlusFiveYears.setFullYear(nowPlusFiveYears.getFullYear() + 5);
        return formatDate(nowPlusFiveYears, "yyyy-MM-dd", "en");
    }

    public get concepts(): SelectionListItem[] {
        var keys = [2, 3, 4, 5];
        return keys.map((key) => {
            return <SelectionListItem> {
                value: key,
                langKey: "permit.team.createCertificate.conceptTypes." + key,
            };
        });
    }

    constructor(private fb: UntypedFormBuilder, private modalService: HaModalService) {
    }

    ngOnInit(): void {
        if (this.requireEmailAddress && this.form.get("email") == null) {
            this.form.addControl(
                "email",
                new UntypedFormControl("", [Validators.required, Validators.pattern("^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$")]),
            );
        }
    }

    public getCurrentActiveConcept(): ConceptType {
        if (!this.form || !this.form.get("concept").value) {
            return undefined;
        }

        return this.form.get("concept").value as ConceptType;
    }

    public onChange(selection: string): void {
        var certificateId = this.form.get("certificateIdentifier");
        if (selection == "3") {
            certificateId.setValidators(null);
        } else {
            certificateId.setValidators(Validators.required);
        }
        certificateId.updateValueAndValidity();
    }

    public openCertificateIdentifierHintModal(event: MouseEvent): void {
        const element = event.target as HTMLElement;
        if (element.tagName.toLowerCase() === "a") {
            this.modalService.open(CertificateIdentifierHintModalComponent, true);
        }
    }
}
